import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { rupees } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function BillingReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ payment_status: "pending", status: "open" });
    const [attempts, setAttempts] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const poll = async (n = 0) => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                if (cancelled) return;
                setStatus(data);
                setAttempts(n + 1);
                if (data.payment_status === "paid" || data.status === "expired" || data.payment_status === "failed") {
                    setDone(true);
                    return;
                }
                if (n + 1 >= MAX_ATTEMPTS) { setDone(true); return; }
                setTimeout(() => poll(n + 1), POLL_INTERVAL);
            } catch (_e) {
                if (n + 1 >= MAX_ATTEMPTS) { setDone(true); return; }
                setTimeout(() => poll(n + 1), POLL_INTERVAL);
            }
        };
        poll(0);
        return () => { cancelled = true; };
    }, [sessionId]);

    const paid = status.payment_status === "paid";
    const failed = status.payment_status === "failed" || status.status === "expired";

    return (
        <div className="min-h-screen flex items-center justify-center px-6 bg-background" data-testid="billing-return">
            <div className="max-w-md w-full text-center">
                {!done ? (
                    <>
                        <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
                        <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">Confirming your payment…</h1>
                        <p className="mt-2 text-sm text-muted-foreground font-mono">attempt {attempts} / {MAX_ATTEMPTS}</p>
                    </>
                ) : paid ? (
                    <>
                        <CheckCircle2 className="h-12 w-12 mx-auto text-primary" data-testid="billing-paid-icon" />
                        <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">Payment received.</h1>
                        <p className="mt-3 text-sm text-muted-foreground">
                            Thank you. Your advisor plan is active for the next year.
                        </p>
                        <p className="mt-2 text-sm font-mono">{rupees(status.amount)} {(status.currency || "").toUpperCase()}</p>
                        <Link
                            to="/advisor"
                            data-testid="go-advisor"
                            className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold"
                        >
                            <ArrowLeft className="h-4 w-4" /> Open Advisor Console
                        </Link>
                    </>
                ) : failed ? (
                    <>
                        <XCircle className="h-12 w-12 mx-auto text-destructive" data-testid="billing-failed-icon" />
                        <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">Payment didn't go through.</h1>
                        <p className="mt-3 text-sm text-muted-foreground">
                            No charge was made. Try again or use a different card.
                        </p>
                        <Link
                            to="/billing"
                            className="mt-8 inline-flex items-center gap-2 border border-border bg-card px-6 py-3 rounded-full text-sm font-semibold"
                        >
                            <ArrowLeft className="h-4 w-4" /> Back to plans
                        </Link>
                    </>
                ) : (
                    <>
                        <Loader2 className="h-10 w-10 mx-auto text-muted-foreground" />
                        <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">Still processing</h1>
                        <p className="mt-3 text-sm text-muted-foreground">
                            We'll mark the plan active as soon as Stripe confirms. You can safely leave this page.
                        </p>
                        <Link
                            to="/dashboard"
                            className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold"
                        >
                            Go to dashboard
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
